import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

const MAX_BYTES = 5 * 1024 * 1024;

export type CoverImageUploadFn = (
  file: File,
  academyId: string,
  storageKey: string
) => Promise<string>;

export interface UseCoverImageUploadOptions {
  academyId: string | undefined;
  /** Existing entity id when editing; omit when creating (uses a pending storage key). */
  entityId?: string | null;
  imageUrl: string;
  setImageUrl: (url: string) => void;
  upload: CoverImageUploadFn;
}

export interface UseCoverImageUploadResult {
  displayUrl: string;
  uploading: boolean;
  handleFileSelect: (file: File) => void;
  handleRemove: () => void;
  clear: () => void;
  resolveImagePayloadForSubmit: () => { imageUrl?: string | null };
}

function validateImageFile(file: File): boolean {
  if (!file.type.startsWith('image/')) {
    toast.error('Please choose an image file');
    return false;
  }
  if (file.size > MAX_BYTES) {
    toast.error('Image must be 5MB or smaller');
    return false;
  }
  return true;
}

/**
 * Cover image picker with immediate compress + upload on file select.
 * Upload state is cancelled on clear/remove so the UI cannot stay stuck loading.
 */
export function useCoverImageUpload({
  academyId,
  entityId,
  imageUrl,
  setImageUrl,
  upload,
}: UseCoverImageUploadOptions): UseCoverImageUploadResult {
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const removeRequestedRef = useRef(false);
  const uploadGenRef = useRef(0);

  const revokePreview = useCallback((url: string | null) => {
    if (url) URL.revokeObjectURL(url);
  }, []);

  const clear = useCallback(() => {
    uploadGenRef.current += 1;
    setUploading(false);
    removeRequestedRef.current = false;
    setPendingPreview((prev) => {
      revokePreview(prev);
      return null;
    });
  }, [revokePreview]);

  useEffect(() => {
    return () => {
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    };
  }, [pendingPreview]);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!validateImageFile(file)) return;
      if (!academyId) {
        toast.error('Unable to upload image');
        return;
      }

      const gen = ++uploadGenRef.current;
      removeRequestedRef.current = false;

      setPendingPreview((prev) => {
        revokePreview(prev);
        return URL.createObjectURL(file);
      });

      const storageKey = entityId ?? `pending-${globalThis.crypto.randomUUID()}`;

      setUploading(true);
      void (async () => {
        try {
          const url = await upload(file, academyId, storageKey);
          if (gen !== uploadGenRef.current) return;
          setImageUrl(url);
          setPendingPreview((prev) => {
            revokePreview(prev);
            return null;
          });
        } catch (error: unknown) {
          if (gen !== uploadGenRef.current) return;
          const message = error instanceof Error ? error.message : 'Failed to upload image';
          toast.error(message);
          setPendingPreview((prev) => {
            revokePreview(prev);
            return null;
          });
        } finally {
          if (gen === uploadGenRef.current) {
            setUploading(false);
          }
        }
      })();
    },
    [academyId, entityId, upload, setImageUrl, revokePreview]
  );

  const handleRemove = useCallback(() => {
    uploadGenRef.current += 1;
    setUploading(false);
    removeRequestedRef.current = true;
    setImageUrl('');
    setPendingPreview((prev) => {
      revokePreview(prev);
      return null;
    });
  }, [setImageUrl, revokePreview]);

  const resolveImagePayloadForSubmit = useCallback((): { imageUrl?: string | null } => {
    if (uploading) {
      throw new Error('Please wait for the image upload to finish');
    }
    if (removeRequestedRef.current) {
      removeRequestedRef.current = false;
      return { imageUrl: null };
    }
    const u = imageUrl?.trim();
    return u ? { imageUrl: u } : {};
  }, [imageUrl, uploading]);

  const displayUrl = pendingPreview ?? imageUrl ?? '';

  return {
    displayUrl,
    uploading,
    handleFileSelect,
    handleRemove,
    clear,
    resolveImagePayloadForSubmit,
  };
}
